/**
 * Lehrkraft-/Bewerbungs-Bereich: targets of notifications. Notifications carry office paths (/substitution-inquiries-table,
 * /payoff, /today?filter=open …); teacher-only and applicant logins cannot open them (the router guard would silently redirect
 * to „Heute“). While the portal layout is shown (setNotificationPortal), the paths are mapped to the matching portal pages;
 * a path without a portal counterpart gives undefined (the notification is shown without link).
 * Pure module (no store / Vue imports), checked in tests/teacherPortal/portalNotificationPaths.check.ts.
 */
export type NotificationPortal = 'teacher' | 'applicant' | null;

let activePortal: NotificationPortal = null;

/** Set by PortalLayout for teacher-only / applicant logins (office logins keep the office paths) */
export function setNotificationPortal(portal: NotificationPortal): void {
    activePortal = portal;
}

export function notificationPortal(): NotificationPortal {
    return activePortal;
}

function splitPath(path: string): { pathname: string; query: URLSearchParams } {
    const index = path.indexOf('?');
    return index < 0
        ? {pathname: path, query: new URLSearchParams()}
        : {pathname: path.slice(0, index), query: new URLSearchParams(path.slice(index + 1))};
}

const starts = (pathname: string, ...prefixes: string[]) => prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    || pathname.startsWith(`${prefix}?`) || (prefix.endsWith('-') && pathname.startsWith(prefix)));

function teacherPath(path: string): string | undefined {
    const {pathname, query} = splitPath(path);
    if (starts(pathname, '/my-teaching')) {
        return path;
    }
    if (starts(pathname, '/substitution-inquiries-', '/substitution-inquiries', '/substitution-plan', '/substitutions')) {
        return '/my-teaching/substitutions';
    }
    if (starts(pathname, '/takeover-offers')) {
        return '/my-teaching/substitutions?tab=open';
    }
    if (starts(pathname, '/payoff', '/payoff-audit', '/payoffs')) {
        return '/my-teaching/payoff';
    }
    if (starts(pathname, '/today')) {
        // Reminder "Kursprotokolle offen" → own course logs (backlog, newest day)
        if (query.get('filter') === 'open' || query.get('backlog')) {
            const day = query.get('day');
            return `/my-teaching/course-logs?backlog=1${day ? `&day=${encodeURIComponent(day)}` : ''}`;
        }
        return '/my-teaching/today';
    }
    if (starts(pathname, '/schedule', '/my-appointments', '/courses')) {
        return '/my-teaching/schedule';
    }
    if (starts(pathname, '/trainings', '/planning/handout-events')) {
        return '/my-teaching/help';
    }
    if (starts(pathname, '/employees')) {
        return '/my-teaching/profile';
    }
    if (starts(pathname, '/todos') || pathname === '/' || pathname === '') {
        return '/my-teaching/today';
    }
    return undefined;
}

function applicantPath(path: string): string | undefined {
    const {pathname} = splitPath(path);
    if (starts(pathname, '/my-application')) {
        return path;
    }
    if (starts(pathname, '/trainings')) {
        return '/my-application/trainings';
    }
    if (starts(pathname, '/employees', '/recruiter/applicants')) {
        return '/my-application/profile';
    }
    if (starts(pathname, '/todos') || pathname === '/' || pathname === '') {
        return '/my-application';
    }
    return undefined;
}

/** Portal page for an office path of a notification (unchanged without portal) */
export function portalNotificationPath(path: string | undefined, portal: NotificationPortal = activePortal): string | undefined {
    if (!path || !portal) {
        return path;
    }
    return portal === 'teacher' ? teacherPath(path) : applicantPath(path);
}
